import React, { useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  Inbox,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Mail,
  Phone as PhoneIcon,
  FolderTree,
  Tag,
  Upload,
  Image as ImageIcon,
  BarChart3,
  Eye,
  TrendingUp,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const isAdmin = (user) => user?.role === "admin";

// ---------------- Dashboard Stats ----------------
const StatsView = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats)
    return (
      <div className="text-[#9a958d]" data-testid="admin-stats-loading">
        Loading stats...
      </div>
    );
  const items = [
    { label: "Total Products", value: stats.products, testId: "stat-products" },
    { label: "Categories", value: stats.categories, testId: "stat-categories" },
    { label: "Total Inquiries", value: stats.inquiries, testId: "stat-inquiries" },
    { label: "New Inquiries", value: stats.new_inquiries, testId: "stat-new-inquiries", accent: true },
  ];
  return (
    <div>
      <h2 className="font-display text-3xl font-black">Dashboard</h2>
      <p className="mt-2 text-sm text-muted-warm">Overview of your AREL store.</p>
      <div className="mt-8 grid gap-4 md:grid-cols-4">
        {items.map((s) => (
          <div key={s.label} data-testid={s.testId} className="arel-card rounded-xl p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-warm">{s.label}</div>
            <div className={`mt-3 font-display text-4xl font-black ${s.accent ? "text-[#dd0447]" : ""}`}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- Image Upload Widget ----------------
const ImageUploadWidget = ({ value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  return (
    <div className="space-y-3" data-testid={`${testId}-widget`}>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-line-strong bg-arel-bg p-6 text-center transition-colors hover:border-[#dd0447]"
      >
        {value ? (
          <div className="flex w-full items-center gap-3">
            <img src={value} alt="preview" className="h-20 w-20 rounded-md border border-line object-cover product-tile" />
            <div className="flex-1 truncate text-left">
              <div className="text-xs uppercase tracking-widest text-muted-warm">Current image</div>
              <div className="mt-1 truncate text-sm text-ink" data-testid={`${testId}-current-url`}>{value}</div>
            </div>
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={() => onChange("")}
              className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-red-500 hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <ImageIcon className="h-8 w-8 text-muted-warm" />
            <div className="text-sm text-ink">Drag &amp; drop an image, or</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          data-testid={`${testId}-file-input`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          type="button"
          data-testid={`${testId}-choose`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-full border border-line-strong bg-white px-4 py-2 text-xs font-semibold text-ink hover:border-[#dd0447] hover:text-[#dd0447] disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {uploading ? "Uploading…" : value ? "Replace image" : "Choose file"}
        </button>
      </div>
      <div>
        <label className="mb-1 block text-[10px] uppercase tracking-widest text-muted-warm">Or paste an image URL</label>
        <input
          data-testid={`${testId}-url`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... or /api/images/..."
          className="arel-input w-full rounded-md px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
};

// ---------------- Product Form Modal ----------------
const ProductFormModal = ({ open, onClose, onSaved, product, categories, brands }) => {
  const [form, setForm] = useState({
    name: "",
    model: "",
    brand: brands[0]?.name || "AREL",
    category_slug: categories[0]?.slug || "",
    short_description: "",
    description: "",
    image_url: "",
    gallery: [],
    video_url: "",
    features_text: "",
    specs_text: "",
    warranty: "",
    featured: false,
    in_stock: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || "",
        model: product.model || "",
        brand: product.brand || brands[0]?.name || "AREL",
        category_slug: product.category_slug || categories[0]?.slug || "",
        short_description: product.short_description || "",
        description: product.description || "",
        image_url: product.image_url || "",
        gallery: (product.gallery || []).slice(0, 4),
        video_url: product.video_url || "",
        features_text: (product.features || []).join("\n"),
        specs_text: Object.entries(product.specifications || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n"),
        warranty: product.warranty || "",
        featured: !!product.featured,
        in_stock: product.in_stock !== false,
      });
    } else {
      setForm({
        name: "",
        model: "",
        brand: brands[0]?.name || "AREL",
        category_slug: categories[0]?.slug || "",
        short_description: "",
        description: "",
        image_url: "",
        gallery: [],
        video_url: "",
        features_text: "",
        specs_text: "",
        warranty: "",
        featured: false,
        in_stock: true,
      });
    }
  }, [product, categories, brands, open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const features = form.features_text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const specifications = {};
      form.specs_text.split("\n").forEach((line) => {
        const idx = line.indexOf(":");
        if (idx > 0) {
          const k = line.slice(0, idx).trim();
          const v = line.slice(idx + 1).trim();
          if (k && v) specifications[k] = v;
        }
      });
      const payload = {
        name: form.name,
        model: form.model,
        brand: form.brand,
        category_slug: form.category_slug,
        short_description: form.short_description,
        description: form.description,
        image_url: form.image_url,
        gallery: (form.gallery || []).filter(Boolean).slice(0, 4),
        video_url: form.video_url,
        features,
        specifications,
        warranty: form.warranty,
        featured: form.featured,
        in_stock: form.in_stock,
      };
      if (product) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="admin-product-modal"
    >
      <div
        className="my-10 w-full max-w-2xl rounded-2xl border border-line bg-paper p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-2xl font-bold">{product ? "Edit product" : "New product"}</h3>
          <button onClick={onClose} className="text-muted-warm hover:text-ink" data-testid="admin-product-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Name*</label>
              <input required data-testid="admin-product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Model</label>
              <input data-testid="admin-product-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Brand*</label>
              <select data-testid="admin-product-brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm">
                {brands.map((b) => <option key={b.slug} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Category*</label>
              <select data-testid="admin-product-category" required value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm">
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-muted-warm">Cover Image*</label>
            <ImageUploadWidget
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
              testId="admin-product-image"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-muted-warm">
                Additional Images ({(form.gallery || []).length}/4)
              </label>
              {(form.gallery || []).length < 4 && (
                <button
                  type="button"
                  data-testid="admin-product-add-gallery-slot"
                  onClick={() => setForm({ ...form, gallery: [...(form.gallery || []), ""] })}
                  className="text-xs font-semibold text-[#dd0447] hover:underline"
                >
                  + Add image slot
                </button>
              )}
            </div>
            {(form.gallery || []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-line-strong bg-arel-bg p-4 text-center text-xs text-muted-warm">
                Add up to 4 extra photos (side/back/angle shots). Product detail page will show them in a gallery with the cover.
              </div>
            ) : (
              <div className="space-y-3">
                {form.gallery.map((g, idx) => (
                  <div key={idx} className="rounded-lg border border-line bg-arel-bg p-3" data-testid={`admin-product-gallery-slot-${idx}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-[11px] font-bold uppercase tracking-widest text-muted-warm">
                        Image {idx + 2}
                      </div>
                      <button
                        type="button"
                        data-testid={`admin-product-gallery-remove-${idx}`}
                        onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, i) => i !== idx) })}
                        className="rounded-md border border-line-strong p-1.5 text-muted-warm hover:border-red-500 hover:text-red-500"
                        aria-label="Remove image"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <ImageUploadWidget
                      value={g}
                      onChange={(url) => {
                        const next = [...form.gallery];
                        next[idx] = url;
                        setForm({ ...form, gallery: next });
                      }}
                      testId={`admin-product-gallery-${idx}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Video Link (YouTube URL — optional)</label>
            <input
              data-testid="admin-product-video-url"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              className="arel-input w-full rounded-md px-3 py-2.5 text-sm"
              placeholder="https://youtube.com/watch?v=... or https://youtu.be/... or /shorts/..."
            />
            <div className="mt-1 text-[11px] text-muted-warm">
              Any YouTube URL works (regular, youtu.be, or Shorts). It will be embedded on the product page.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Short description</label>
            <input data-testid="admin-product-short-desc" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Full description</label>
            <textarea data-testid="admin-product-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Features (one per line)</label>
              <textarea data-testid="admin-product-features" rows={4} value={form.features_text} onChange={(e) => setForm({ ...form, features_text: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" placeholder="Inverter Compressor&#10;Frost Free" />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Specs (key: value per line)</label>
              <textarea data-testid="admin-product-specs" rows={4} value={form.specs_text} onChange={(e) => setForm({ ...form, specs_text: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" placeholder="Capacity: 265 L&#10;Warranty: 1 Year" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">Warranty</label>
            <input data-testid="admin-product-warranty" value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" placeholder="1 Year Comprehensive · 5 Year Compressor" />
          </div>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-ink-2">
              <input data-testid="admin-product-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#dd0447]" />
              Featured
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-ink-2">
              <input data-testid="admin-product-in-stock" type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} className="h-4 w-4 accent-[#dd0447]" />
              In stock
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="arel-outline-btn rounded-full px-5 py-2.5 text-sm font-semibold">Cancel</button>
            <button type="submit" disabled={saving} data-testid="admin-product-save" className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------- Products View ----------------
const ProductsView = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/products?all=true"), api.get("/categories"), api.get("/brands")])
      .then(([p, c, b]) => {
        setProducts(p.data);
        setCategories(c.data);
        setBrands(b.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleLive = async (p) => {
    const nextLive = !(p.is_live !== false); // treat missing as live
    // optimistic update
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_live: nextLive } : x)));
    try {
      await api.put(`/products/${p.id}`, { is_live: nextLive });
      toast.success(nextLive ? `"${p.name}" is now live` : `"${p.name}" is now unlive (hidden from site)`);
    } catch (err) {
      // revert
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_live: !nextLive } : x)));
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  return (
    <div data-testid="admin-products-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl font-black">Products</h2>
          <p className="mt-2 text-sm text-muted-warm">Manage your product catalog.</p>
        </div>
        <button
          data-testid="admin-add-product-btn"
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="mt-8 arel-card overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-[#f4f1ec] text-left text-xs uppercase tracking-widest text-muted-warm">
              <tr>
                <th className="px-6 py-4">Product</th>
                <th className="px-6 py-4">Brand</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Featured</th>
                <th className="px-6 py-4">Stock</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#9a958d]">Loading...</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#9a958d]">No products yet.</td></tr>
              ) : products.map((p) => (
                <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className={`border-b border-line last:border-0 hover:bg-[#f4f1ec] ${p.is_live === false ? "bg-[#faf5f4]/60" : ""}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {p.image_url && (
                        <img src={p.image_url} alt="" className={`h-10 w-10 rounded object-cover ${p.is_live === false ? "opacity-50 grayscale" : ""}`} />
                      )}
                      <div>
                        <div className={`font-semibold ${p.is_live === false ? "text-muted-warm line-through" : "text-ink"}`}>{p.name}</div>
                        <div className="text-xs text-muted-warm">{p.model || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white ${p.brand === "Daikin" ? "bg-[#0097e0]" : "bg-[#dd0447]"}`}>
                      {p.brand || "AREL"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-muted-warm">{categories.find((c) => c.slug === p.category_slug)?.name || p.category_slug}</td>
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={p.is_live !== false}
                      onClick={() => toggleLive(p)}
                      data-testid={`admin-toggle-live-${p.id}`}
                      title={p.is_live !== false ? "Live on website — click to hide" : "Hidden from website — click to make live"}
                      className="group inline-flex items-center gap-2 focus:outline-none"
                    >
                      <span
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                          p.is_live !== false ? "bg-emerald-500" : "bg-[#c9c1b6]"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            p.is_live !== false ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                          p.is_live !== false
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-[#f4d9e2] text-[#dd0447]"
                        }`}
                        data-testid={`admin-status-badge-${p.id}`}
                      >
                        {p.is_live !== false ? "Live" : "Unlive"}
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    {p.featured ? <span className="rounded-full bg-[#dd0447] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Yes</span> : <span className="text-[#9a958d]">No</span>}
                  </td>
                  <td className="px-6 py-4">
                    {p.in_stock ? <span className="text-emerald-600">In stock</span> : <span className="text-[#9a958d]">Out</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`admin-edit-product-${p.id}`} onClick={() => { setEditing(p); setModalOpen(true); }} className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-[#dd0447] hover:text-ink">
                        <Pencil className="h-4 w-4" />
                      </button>
                      {isAdmin(user) && (
                        <button data-testid={`admin-delete-product-${p.id}`} onClick={() => remove(p)} className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-red-500 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ProductFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        product={editing}
        categories={categories}
        brands={brands}
      />
    </div>
  );
};

// ---------------- Generic Simple CRUD Modal (used by Categories & Brands) ----------------
const SimpleModal = ({ open, onClose, title, fields, initial, onSave }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(initial || {});
  }, [initial, open]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm" onClick={onClose} data-testid="admin-simple-modal">
      <div className="my-10 w-full max-w-lg rounded-2xl border border-line bg-paper p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-2xl font-bold">{title}</h3>
          <button onClick={onClose} className="text-muted-warm hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="mb-1 block text-xs uppercase tracking-widest text-muted-warm">{f.label}{f.required && "*"}</label>
              {f.type === "image" ? (
                <ImageUploadWidget
                  value={form[f.name] || ""}
                  onChange={(v) => setForm({ ...form, [f.name]: v })}
                  testId={`admin-modal-${f.name}`}
                />
              ) : f.type === "color" ? (
                <input
                  type="color"
                  data-testid={`admin-modal-input-${f.name}`}
                  value={form[f.name] || "#dd0447"}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="h-10 w-20 cursor-pointer rounded border border-line-strong"
                />
              ) : f.type === "number" ? (
                <input type="number" data-testid={`admin-modal-input-${f.name}`} value={form[f.name] ?? 0} onChange={(e) => setForm({ ...form, [f.name]: parseInt(e.target.value || "0", 10) })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" />
              ) : f.type === "textarea" ? (
                <textarea rows={3} data-testid={`admin-modal-input-${f.name}`} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="arel-input w-full rounded-md px-3 py-2.5 text-sm" />
              ) : (
                <input
                  data-testid={`admin-modal-input-${f.name}`}
                  required={f.required}
                  value={form[f.name] || ""}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="arel-input w-full rounded-md px-3 py-2.5 text-sm"
                  placeholder={f.placeholder || ""}
                />
              )}
              {f.hint && <div className="mt-1 text-[11px] text-muted-warm">{f.hint}</div>}
            </div>
          ))}
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="arel-outline-btn rounded-full px-5 py-2.5 text-sm font-semibold">Cancel</button>
            <button type="submit" disabled={saving} data-testid="admin-modal-save" className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------- Categories View ----------------
const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const CategoriesView = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/categories").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const payload = {
      slug: (form.slug || slugify(form.name)).trim(),
      name: form.name,
      description: form.description || "",
      image_url: form.image_url || "",
      order: form.order ?? 0,
    };
    if (editing) {
      await api.put(`/categories/${editing.id}`, payload);
      toast.success("Category updated");
    } else {
      await api.post("/categories", payload);
      toast.success("Category created");
    }
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-categories-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl font-black">Categories</h2>
          <p className="mt-2 text-sm text-muted-warm">Add, rename or reorder product categories.</p>
        </div>
        <button
          data-testid="admin-add-category-btn"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" /> Add Category
        </button>
      </div>

      <div className="mt-8 arel-card overflow-hidden rounded-xl">
        <table className="w-full text-sm">
          <thead className="border-b border-line bg-[#f4f1ec] text-left text-xs uppercase tracking-widest text-muted-warm">
            <tr>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Slug</th>
              <th className="px-6 py-4">Order</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-[#9a958d]">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-[#9a958d]">No categories yet.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} data-testid={`admin-category-row-${c.slug}`} className="border-b border-line last:border-0 hover:bg-[#f4f1ec]">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {c.image_url && <img src={c.image_url} alt="" className="h-9 w-9 rounded object-cover" />}
                    <div>
                      <div className="font-semibold text-ink">{c.name}</div>
                      {c.description && <div className="text-xs text-muted-warm truncate max-w-md">{c.description}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-muted-warm font-mono text-xs">{c.slug}</td>
                <td className="px-6 py-4 text-muted-warm">{c.order}</td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`admin-edit-category-${c.slug}`} onClick={() => { setEditing(c); setOpen(true); }} className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-[#dd0447] hover:text-ink">
                      <Pencil className="h-4 w-4" />
                    </button>
                    {isAdmin(user) && (
                      <button data-testid={`admin-delete-category-${c.slug}`} onClick={() => remove(c)} className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-red-500 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SimpleModal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit category" : "New category"}
        initial={editing || { order: (items[items.length - 1]?.order ?? 0) + 1 }}
        fields={[
          { name: "name", label: "Name", required: true, placeholder: "Refrigerators" },
          { name: "slug", label: "Slug (URL-safe)", placeholder: "auto-generated if blank", hint: "Lowercase, hyphenated. Leave blank to auto-generate from name." },
          { name: "description", label: "Description", type: "textarea" },
          { name: "image_url", label: "Category image", type: "image" },
          { name: "order", label: "Sort order", type: "number" },
        ]}
        onSave={save}
      />
    </div>
  );
};

// ---------------- Brands View ----------------
const BrandsView = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/brands").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const payload = {
      slug: (form.slug || slugify(form.name)).trim(),
      name: form.name,
      color: form.color || "#dd0447",
      order: form.order ?? 0,
    };
    if (editing) {
      await api.put(`/brands/${editing.id}`, payload);
      toast.success("Brand updated");
    } else {
      await api.post("/brands", payload);
      toast.success("Brand created");
    }
    load();
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    try {
      await api.delete(`/brands/${b.id}`);
      toast.success("Brand deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-brands-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl font-black">Brands</h2>
          <p className="mt-2 text-sm text-muted-warm">Brands used across the product catalog.</p>
        </div>
        <button
          data-testid="admin-add-brand-btn"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" /> Add Brand
        </button>
      </div>

      <div className="mt-8 arel-card overflow-hidden rounded-xl">
        <table className="w-full text-sm">
          <thead className="border-b border-line bg-[#f4f1ec] text-left text-xs uppercase tracking-widest text-muted-warm">
            <tr>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Slug</th>
              <th className="px-6 py-4">Color</th>
              <th className="px-6 py-4">Order</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-[#9a958d]">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-[#9a958d]">No brands yet.</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} data-testid={`admin-brand-row-${b.slug}`} className="border-b border-line last:border-0 hover:bg-[#f4f1ec]">
                <td className="px-6 py-4 font-semibold text-ink">{b.name}</td>
                <td className="px-6 py-4 font-mono text-xs text-muted-warm">{b.slug}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full border border-line" style={{ background: b.color }} />
                    <span className="text-xs text-muted-warm">{b.color}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-muted-warm">{b.order}</td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`admin-edit-brand-${b.slug}`} onClick={() => { setEditing(b); setOpen(true); }} className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-[#dd0447] hover:text-ink">
                      <Pencil className="h-4 w-4" />
                    </button>
                    {isAdmin(user) && (
                      <button data-testid={`admin-delete-brand-${b.slug}`} onClick={() => remove(b)} className="rounded-md border border-line-strong p-2 text-muted-warm hover:border-red-500 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SimpleModal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit brand" : "New brand"}
        initial={editing || { color: "#dd0447", order: (items[items.length - 1]?.order ?? 0) + 1 }}
        fields={[
          { name: "name", label: "Brand name", required: true, placeholder: "AREL" },
          { name: "slug", label: "Slug", placeholder: "auto-generated if blank" },
          { name: "color", label: "Accent color", type: "color" },
          { name: "order", label: "Sort order", type: "number" },
        ]}
        onSave={save}
      />
    </div>
  );
};

// ---------------- Inquiries View ----------------
const InquiriesView = () => {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/inquiries").then((r) => setInquiries(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (inq, status) => {
    try {
      await api.patch(`/inquiries/${inq.id}?status=${status}`);
      toast.success(`Marked as ${status}`);
      load();
      if (selected?.id === inq.id) setSelected({ ...selected, status });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (inq) => {
    if (!window.confirm(`Delete inquiry from ${inq.name}?`)) return;
    try {
      await api.delete(`/inquiries/${inq.id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-inquiries-view">
      <h2 className="font-display text-3xl font-black">Inquiries</h2>
      <p className="mt-2 text-sm text-muted-warm">Customer messages and dealer applications.</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr,1fr]">
        <div className="arel-card overflow-hidden rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-[#f4f1ec] text-left text-xs uppercase tracking-widest text-muted-warm">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#9a958d]">Loading...</td></tr>
                ) : inquiries.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#9a958d]">No inquiries yet.</td></tr>
                ) : inquiries.map((inq) => (
                  <tr key={inq.id} data-testid={`admin-inquiry-row-${inq.id}`} onClick={() => setSelected(inq)} className={`cursor-pointer border-b border-line last:border-0 hover:bg-[#f4f1ec] ${selected?.id === inq.id ? "bg-[#f4f1ec]" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-ink">{inq.name}</div>
                      <div className="text-xs text-[#9a958d]">{inq.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full border border-line-strong px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-warm">
                        {inq.inquiry_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-warm text-xs">{inq.product_name || "-"}</td>
                    <td className="px-4 py-3">
                      {inq.status === "new" && <span className="rounded-full bg-[#dd0447] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">New</span>}
                      {inq.status === "read" && <span className="text-muted-warm text-xs">Read</span>}
                      {inq.status === "archived" && <span className="text-[#9a958d] text-xs">Archived</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-warm">{new Date(inq.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="arel-card rounded-xl p-6" data-testid="admin-inquiry-detail">
          {selected ? (
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{selected.inquiry_type}</div>
                  <h3 className="mt-1 font-display text-xl font-bold">{selected.name}</h3>
                </div>
                <button onClick={() => setSelected(null)} className="text-muted-warm hover:text-ink">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-warm">
                  <Mail className="h-4 w-4 text-[#dd0447]" />
                  <a href={`mailto:${selected.email}`} className="hover:text-ink">{selected.email}</a>
                </div>
                <div className="flex items-center gap-2 text-muted-warm">
                  <PhoneIcon className="h-4 w-4 text-[#dd0447]" />
                  <a href={`tel:${selected.phone}`} className="hover:text-ink">{selected.phone}</a>
                </div>
              </div>
              {selected.product_name && (
                <div className="mt-4 rounded-lg border border-line bg-[#f4f1ec] p-3 text-sm">
                  <div className="text-xs text-[#9a958d]">Interested in</div>
                  <div className="font-semibold text-ink">{selected.product_name}</div>
                </div>
              )}
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-[#9a958d]">Message</div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-2">
                  {selected.message}
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {selected.status !== "read" && (
                  <button onClick={() => setStatus(selected, "read")} data-testid="admin-inquiry-mark-read" className="arel-outline-btn rounded-full px-4 py-2 text-xs font-semibold">Mark as read</button>
                )}
                {selected.status !== "archived" && (
                  <button onClick={() => setStatus(selected, "archived")} data-testid="admin-inquiry-archive" className="arel-outline-btn rounded-full px-4 py-2 text-xs font-semibold">Archive</button>
                )}
                {isAdmin(user) && (
                  <button onClick={() => remove(selected)} data-testid="admin-inquiry-delete" className="rounded-full border border-red-500/40 px-4 py-2 text-xs font-semibold text-red-500 hover:border-red-500 hover:bg-red-500/10">Delete</button>
                )}
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-[#9a958d]">Select an inquiry to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------- Analytics View ----------------
const AnalyticsView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/admin/analytics/products")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const products = (data?.products || []).filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(q) ||
      (p.model || "").toLowerCase().includes(q) ||
      (p.brand || "").toLowerCase().includes(q) ||
      (p.category_slug || "").toLowerCase().includes(q)
    );
  });

  const topViews = data?.products?.[0]?.views || 0;
  const totalViews = data?.total_views || 0;
  const productsWithViews = (data?.products || []).filter((p) => p.views > 0).length;

  return (
    <div data-testid="admin-analytics-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl font-black">Product Analytics</h2>
          <p className="mt-2 text-sm text-muted-warm">
            Products ranked by page views — highest customer interest first.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="analytics-refresh-btn"
          className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-[#dd0447] hover:text-[#dd0447]"
        >
          <TrendingUp className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-3">
        <div className="arel-card rounded-xl p-6" data-testid="analytics-total-views">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-warm">Total Product Views</div>
          <div className="mt-3 font-display text-4xl font-black text-[#dd0447]">{totalViews.toLocaleString()}</div>
        </div>
        <div className="arel-card rounded-xl p-6" data-testid="analytics-products-viewed">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-warm">Products With Views</div>
          <div className="mt-3 font-display text-4xl font-black">{productsWithViews}</div>
        </div>
        <div className="arel-card rounded-xl p-6" data-testid="analytics-top-views">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-warm">Top Product Views</div>
          <div className="mt-3 font-display text-4xl font-black">{topViews.toLocaleString()}</div>
        </div>
      </div>

      <div className="mt-8 arel-card overflow-hidden rounded-xl">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="font-display text-lg font-bold">Ranked Products</div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, model, brand..."
            data-testid="analytics-search-input"
            className="w-64 rounded-md border border-line bg-white px-3 py-1.5 text-sm text-ink outline-none focus:border-[#dd0447]"
          />
        </div>

        {loading ? (
          <div className="p-8 text-sm text-muted-warm" data-testid="analytics-loading">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading analytics...
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-sm text-muted-warm" data-testid="analytics-empty">
            No products match your search.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="analytics-table">
            <thead className="bg-[#f4f1ec] text-left text-xs uppercase tracking-widest text-muted-warm">
              <tr>
                <th className="px-6 py-3 w-16">Rank</th>
                <th className="px-6 py-3">Product</th>
                <th className="px-6 py-3">Brand</th>
                <th className="px-6 py-3">Category</th>
                <th className="px-6 py-3 text-right">Views</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, idx) => (
                <tr
                  key={p.id}
                  className="border-t border-line align-middle"
                  data-testid={`analytics-row-${p.id}`}
                >
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-bold ${
                        idx === 0
                          ? "bg-[#dd0447] text-white"
                          : idx < 3
                          ? "bg-[#f4d9e2] text-[#dd0447]"
                          : "bg-[#f4f1ec] text-muted-warm"
                      }`}
                    >
                      #{idx + 1}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          className="h-10 w-10 rounded-md object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#f4f1ec] text-muted-warm">
                          <ImageIcon className="h-4 w-4" />
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-ink">{p.name}</div>
                        {p.model && (
                          <div className="text-xs text-muted-warm">{p.model}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-muted-warm">{p.brand || "—"}</td>
                  <td className="px-6 py-4 text-muted-warm">{p.category_slug || "—"}</td>
                  <td
                    className="px-6 py-4 text-right font-display text-lg font-black"
                    data-testid={`analytics-views-${p.id}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="h-4 w-4 text-muted-warm" />
                      {(p.views || 0).toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------------- Layout ----------------
const AdminDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
    { to: "/admin/products", label: "Products", icon: Package, testId: "admin-nav-products" },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "admin-nav-analytics" },
    { to: "/admin/categories", label: "Categories", icon: FolderTree, testId: "admin-nav-categories" },
    { to: "/admin/brands", label: "Brands", icon: Tag, testId: "admin-nav-brands" },
    { to: "/admin/inquiries", label: "Inquiries", icon: Inbox, testId: "admin-nav-inquiries" },
  ];

  return (
    <div className="min-h-screen bg-arel-bg text-ink" data-testid="admin-dashboard-page">
      <div className="grid min-h-screen md:grid-cols-[260px,1fr]">
        <aside className="border-r border-line bg-[#f4f1ec] p-6">
          <div className="mb-10 flex items-center gap-3">
            <img src="/assets/logo/arel-logo.png" alt="AREL" className="h-9 w-auto" />
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white ${
                isAdmin(user) ? "bg-[#dd0447]" : "bg-[#0097e0]"
              }`}
              data-testid="admin-role-badge"
            >
              {user.role || "user"}
            </span>
          </div>
          <nav className="space-y-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ease-out ${
                    isActive
                      ? "bg-[#dd0447] text-white"
                      : "text-muted-warm hover:bg-[#f4f1ec]"
                  }`
                }
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-10 border-t border-line pt-6">
            <div className="text-xs text-[#9a958d]">Signed in as</div>
            <div className="mt-1 truncate text-sm font-semibold text-ink">{user.email}</div>
            <button
              data-testid="admin-logout-btn"
              onClick={doLogout}
              className="mt-4 inline-flex items-center gap-2 text-sm text-muted-warm hover:text-[#dd0447]"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </aside>

        <main className="p-8 md:p-12">
          <Routes>
            <Route index element={<StatsView />} />
            <Route path="products" element={<ProductsView />} />
            <Route path="analytics" element={<AnalyticsView />} />
            <Route path="categories" element={<CategoriesView />} />
            <Route path="brands" element={<BrandsView />} />
            <Route path="inquiries" element={<InquiriesView />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
